import { useEffect } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { isTokenExpired } from "@/auth/jwt";
import { restoreSession, setCredentials } from "@/auth/store/authSlice";
import { BrandedLoader } from "@/components/shared/BrandedLoader";
import { ChangePasswordModal } from "@/components/shared/ChangePasswordModal";
import { ROUTES } from "./routeConfig";
import { DASHBOARD_BY_SCOPE, findViewAsMatch } from "./viewAs";

interface ProtectedRouteProps {
  allowedRoles?: string[];
}

export function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const dispatch = useAppDispatch();
  const { user, token, restoring } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  const renewable = (!token || isTokenExpired(token)) && !!localStorage.getItem("token") && !!localStorage.getItem("user");

  useEffect(() => {
    if (renewable && !restoring) dispatch(restoreSession());
  }, [renewable, restoring, dispatch]);

  if (restoring || renewable) return <BrandedLoader variant="fullscreen" label="Restoring your session…" />;

  // Every write/read on the backend is re-checked against the SIGNED JWT
  // (see backend/src/middleware/authMiddleware.js) — this client-side gate
  // only controls what UI shell renders, never actual data access. Still,
  // a missing/expired/malformed token must never be treated as "logged in"
  // here, or a stale cached `user` object could flash protected UI.
  if (!token || !user || isTokenExpired(token)) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Every panel must change a temp/first-login password before
  // reaching the rest of the app — enforced here (not just in LoginPage) so it can't be
  // skipped by a page refresh, deep link, or a silently restored session.
  if (user.isFirstLogin) {
    return (
      <ChangePasswordModal
        open
        onOpenChange={() => {}}
        forceChange
        title="Set a new password"
        description="This is your first login (or your password was reset) — choose a new password to continue."
        onSuccess={() => dispatch(setCredentials({ user: { ...user, isFirstLogin: false }, token }))}
      />
    );
  }

  // "View as" — a higher-level admin (SUPER_ADMIN, or ORG viewing their
  // own campuses/institutions) previewing a lower panel read-only, using
  // their own JWT (see routes/viewAs.ts for the full permission table and
  // why this can never grant more than the backend already allows).
  const viewAsMatch = findViewAsMatch(user.role.scope, allowedRoles, searchParams);

  if (allowedRoles && !allowedRoles.includes(user.role.scope) && !viewAsMatch) {
    const ownDashboard = DASHBOARD_BY_SCOPE[user.role.scope];
    return <Navigate to={ownDashboard ?? ROUTES.LOGIN} replace />;
  }

  return <Outlet />;
}
